import "./RegStepPlan.scss";
import { LeftOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Button, Checkbox, Input } from "antd";
import StepCounter from "../StepCounter";
import CancelRegistrationLink from "./CancelRegistrationLink";
import { isValidPhone, isValidZip } from "../../util/utils";
import CustomSelect from "./CustomSelect";
import { ExtendedRegisterMemberDto } from "./RegisterPage";
import { BUSINESS_TYPES, DIGITAL_SIGNAGE, FOOD_DISTRIBUTORS, ORDER_MANAGER, POS_SYSTEMS, PRIMARY_NEEDS, ROLES } from "../../util/constants";
import { Spinner } from "../account/misc/Spinner";

interface RegStepAboutProps {
  onNext: (member: ExtendedRegisterMemberDto) => void;
  onBack: () => void;
  onCancelRegistration: () => void;
  loading: boolean;
  regStep: number;
  regStepTotal: number;
  businessTypeParam: string | null;
  roleParam: string | null;
  businessParam: string | null;
  phoneParam: string | null;
  diffPhoneParam: string | null;
  zipParam: string | null;
  smsOptInParam: boolean;
  posParam: string | null;
  omgrParam: string | null;
  fspParam: string | null;
  dssParam: string | null;
  needsParam: string[];
  popmenu: boolean
}

export default function RegStepAbout({
                                       onNext,
                                       onBack,
                                       onCancelRegistration,
                                       loading,
                                       regStep,
                                       regStepTotal,
                                       businessTypeParam,
                                       roleParam,
                                       businessParam,
                                       phoneParam,
                                       diffPhoneParam,
                                       zipParam,
                                       smsOptInParam,
                                       posParam,
                                       omgrParam,
                                       fspParam,
                                       dssParam,
                                       needsParam,
                                       popmenu
                                     }: RegStepAboutProps) {
  const [valid, setValid] = useState(false);

  const [businessType, setBusinessType] = useState(businessTypeParam);
  const [role, setRole] = useState(roleParam);
  const [business, setBusiness] = useState(businessParam || "");
  const [phone, setPhone] = useState(phoneParam || "");
  const [diffPhone, setDiffPhone] = useState(diffPhoneParam || "");
  const [zip, setZip] = useState(zipParam || "");
  const [smsOptIn] = useState(smsOptInParam);
  const [pos, setPos] = useState(posParam);
  const [omgr, setOmgr] = useState(omgrParam);
  const [fsp, setFsp] = useState(fspParam);
  const [dss, setDss] = useState(dssParam);
  const [needs, setNeeds] = useState(needsParam || []);

  const [isBusinessErr, setBusinessErr] = useState(false);
  const [isPhoneErr, setPhoneErr] = useState(false);
  const [isDiffPhoneErr, setDiffPhoneErr] = useState(false);
  const [isZipErr, setZipErr] = useState(false);

  useEffect(() => {
    let val = true;

    if (!business?.trim() ||
      !phone?.trim() || !isValidPhone(phone) ||
      !zip?.trim() || !isValidZip(zip) ||
      (diffPhone?.trim() && !isValidPhone(diffPhone))) {
      val = false;
    }

    setValid(val);
  }, [diffPhone, business, phone, zip]);

  function onSubmit() {
    if (valid) {
      onNext({
        businessType: businessType || "",
        role: role || "",
        business: (business || "").trim(),
        phone: (phone || "").trim(),
        diffPhone: (diffPhone || "").trim(),
        zip: (zip || "").trim(),
        smsOptIn: smsOptIn,
        pos: pos || "",
        omgr: omgr || "",
        fsp: fsp || "",
        dss: dss || "",
        needs: needs || []
      });
    }
  }

  function toggleNeed(need: string) {
    if (needs.includes(need)) {
      setNeeds(needs.filter(n => n !== need));
    } else {
      setNeeds([...needs, need]);
    }
  }

  return loading ? <div className={"spinner"}><Spinner /></div> : <div className={"step-type step full plan"}>
    <div className={"back"} onClick={onBack}><LeftOutlined /></div>
    <div className={"header"}>
      <StepCounter total={regStepTotal} current={regStep} />
      <h2>Tell us about your business</h2>
      <CancelRegistrationLink onCancel={onCancelRegistration} />
    </div>

    <section className={"about"}>

      <div className={"field-group"}>
        <h3>Account Type</h3>
        <CustomSelect values={BUSINESS_TYPES} placeholder={"What is your business type?"} value={businessType} setValue={setBusinessType} />
        <CustomSelect values={ROLES} placeholder={"What's your role?"} value={role} setValue={setRole} />
      </div>

      <div className={"sep"}></div>

      <div className={"field-group form"}>
        <h3>Contact Information</h3>

        <div className={"blk"}>
          <Input type={"text"} placeholder={"Restaurant or Business Name"} value={business} size={"large"}
                 className={isBusinessErr ? "err" : ""}
                 onChange={ev => {
                   setBusiness(ev.target.value);
                   setBusinessErr(!ev.target.value.trim());
                 }} />
          {isBusinessErr && <label className={"err"}>Enter your business name.</label>}
        </div>

        <div className={"blk"}>
          <Input type={"text"} placeholder={"Best Contact Phone Number"} value={phone} size={"large"}
                 className={isPhoneErr ? "err" : ""}
                 onChange={ev => setPhone(ev.target.value)}
                 onBlur={ev => setPhoneErr(!isValidPhone(ev.target.value))} />
          {isPhoneErr && <label className={"err"}>Enter a valid phone number.</label>}
        </div>

        <div className={"blk"}>
          <Input type={"text"} placeholder={"Business Phone Number (if different)"} value={diffPhone} size={"large"}
                 className={isDiffPhoneErr ? "err" : ""}
                 onChange={ev => setDiffPhone(ev.target.value)}
                 onBlur={ev => setDiffPhoneErr(!!ev.target.value && !isValidPhone(ev.target.value))} />
          {isDiffPhoneErr && <label className={"err"}>Enter a valid phone number.</label>}
        </div>

        <div className={"blk"}>
          <Input type={"text"} placeholder={"Zip Code"} value={zip} size={"large"}
                 className={isZipErr ? "err" : ""}
                 onChange={ev => setZip(ev.target.value)}
                 onBlur={ev => setZipErr(!isValidZip(ev.target.value))} />
          {isZipErr && <label className={"err"}>Enter your zip code.</label>}
        </div>

        {/*<Checkbox checked={smsOptIn} onChange={() => setSmsOptIn(!smsOptIn)} onClick={ev => ev.stopPropagation()}>Opt in to SMS messaging</Checkbox>*/}
      </div>

      {!popmenu && <div className={"sep"}></div>}

      {!popmenu && <div className={"field-group"}>
        <h3>Current Tech Setup</h3>
        <CustomSelect values={POS_SYSTEMS} placeholder={"POS Provider"} value={pos} setValue={setPos} />
        <CustomSelect values={ORDER_MANAGER} placeholder={"3rd Party Order Manager"} value={omgr} setValue={setOmgr} />
        <CustomSelect values={FOOD_DISTRIBUTORS} placeholder={"Food Service Provider"} value={fsp} setValue={setFsp} />
        <CustomSelect values={DIGITAL_SIGNAGE} placeholder={"Digital Signage Provider"} value={dss} setValue={setDss} />
      </div>}

      <div className={"sep"}></div>

      <div className={"field-group"}>
        <h3>What is your primary need?</h3>
        <h4>(check all that apply)</h4>
        <div className={"blk needs"}>
          {PRIMARY_NEEDS.map(need => <Checkbox key={need} checked={needs.includes(need)} onClick={() => toggleNeed(need)}>{need}</Checkbox>)}
        </div>
      </div>
    </section>

    <footer>
      <Button size={"large"} className={"primary"} disabled={!valid} onClick={onSubmit}>Next</Button>
    </footer>
  </div>;
}
